import React, { FC, PropsWithChildren } from 'react'
import { Box, Alert, Text, TertiaryButton } from '@moonpig/launchpad-components'
import { Region } from '@moonpig/web-core-types'
import { formatPrice } from '@moonpig/web-core-utils'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { useLanguage } from '@moonpig/web-core-stores'
import {
  createProductItemGAEvent,
  useOnAddToBasket,
  useRecommendationsLocaleText,
  ProductItemEventEnum,
  NonCardProductTile,
  CardProductTile,
} from '@moonpig/web-shared-products'
import type { ProductTileProduct } from '@moonpig/web-shared-products'
import { Product } from '../../types'

const StyledAlert = styled(Alert)`
  position: absolute;
  z-index: 1;
  opacity: 0.9;
  pointer-events: none;
  top: 15px;
  ${s({
    left: {
      xs: '20px;',
      md: '15px;',
    },

    right: {
      xs: '15px;',
      md: '5px;',
    },
  })}
`

const LinkAs: FC<
  PropsWithChildren<{
    product: ProductTileProduct
    productsLength: number
    onClickHandler: () => void
    index: number
  }>
> = ({ children, product, productsLength, onClickHandler, index, ...rest }) => {
  const { onAddToBasket } = useOnAddToBasket({
    product,
    index,
    productsLength,
    pageName: `product details`,
    componentName: `carousel product tile`,
  })

  return (
    <TertiaryButton
      {...rest}
      type="button"
      onClick={() => {
        onClickHandler()
        onAddToBasket({
          isProductCustomisable: product.customisable,
          productId: product.id,
          variant: product.masterVariant,
          quantity: 1,
          selectedAddon: null,
        })
      }}
    >
      {children}
    </TertiaryButton>
  )
}

export const ProductTileWithAddToBasketHandler: FC<{
  product: Product
  index: number
  region: Region
  parentProductId: string
  productsLength: number
  carouselTrackingInfo: {
    carouselTitle: string
    carouselIndex: number
    carouselLength: number
  }
}> = ({ product, index, region, productsLength, carouselTrackingInfo }) => {
  const localiseRecommendationsText = useRecommendationsLocaleText()

  const language = useLanguage()
  const [showNotification, setShowNotification] = React.useState(false)

  const price = formatPrice(
    product.masterVariant.price.centAmount,
    product.masterVariant.price.fractionDigits,
    product.masterVariant.price.currencyCode,
    undefined,
    language,
  )

  const fullPrice = formatPrice(
    product.masterVariant.fullPrice.centAmount,
    product.masterVariant.fullPrice.fractionDigits,
    product.masterVariant.fullPrice.currencyCode,
    undefined,
    language,
  )

  const discountedPercentage = product.masterVariant.discountedPercentage

  const productURL = `/${region}/${product.category.slug}/p/${
    product.slug
  }/${product.id.toLowerCase()}/`

  const image = {
    src: product.masterVariant.images[0].url,
    alt: product.title,
  }

  const trackSelectEvent = () => {
    const { carouselTitle, carouselIndex, carouselLength } =
      carouselTrackingInfo
    const listName = `product details | ${carouselTitle} | carousel | ${carouselIndex}/${carouselLength}`
    const label = `${listName} | ${index + 1}/${productsLength} | ${product.id}`
    const variant = product.masterVariant.title

    trackGAEvent(
      createProductItemGAEvent({
        eventType: ProductItemEventEnum.SELECT_ITEM,
        product,
        index,
        label,
        listName,
        variant,
      }),
    )
  }

  return (
    <Box py={{ xs: 4, md: 5 }} height="100%" position="relative">
      {showNotification && (
        <StyledAlert variant="success">
          <Text as="span" typography="typeBodyCaption">
            {localiseRecommendationsText('recommendations.added_to_basket')}
          </Text>
        </StyledAlert>
      )}
      {product.category.id === 1 ? (
        <CardProductTile
          href={productURL}
          image={image}
          onClick={trackSelectEvent}
        />
      ) : (
        <NonCardProductTile
          productId={product.id}
          href={productURL}
          title={product.title}
          price={price}
          fullPrice={fullPrice}
          discountedPercentage={discountedPercentage}
          image={image}
          rating={product.rating || undefined}
          // eslint-disable-next-line react/no-unstable-nested-components
          linkAs={({ children, to, ...rest }) => (
            <LinkAs
              index={index}
              product={product}
              to={to}
              onClickHandler={() => setShowNotification(true)}
              productsLength={productsLength}
              {...rest}
            >
              {children}
            </LinkAs>
          )}
          titleAs="h3"
          priceAs="p"
          onClick={trackSelectEvent}
        />
      )}
    </Box>
  )
}
